// app/javascript/controllers/image_selector_controller.js
// Product photos (seller/products/_images_section.html.erb).
//  - Camera and gallery pickers (two <input type=file>, opened by buttons).
//  - Direct Upload to storage on selection; only signed blob ids are posted.
//    The hidden signed-id input lives INSIDE each new thumbnail, so the form
//    posts them in the thumbnail order - drag to reorder before saving.
//  - Saved images: drag to reorder (SortableJS, touch friendly) or "Make
//    main"; both persist at once via JSON, no page reload.
//  - Delete (single / bulk) keeps the existing confirm modal + Turbo form.
import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = [
    "cameraInput", "galleryInput", "newPreview", "newCount", "newList", "errorBox",
    "deleteButton", "deleteCount", "existingList", "imageCard", "badgeSlot", "emptyState"
  ]
  static values = {
    deleteUrl: String,
    reorderUrl: String,
    directUploadUrl: String,
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    required: { type: Boolean, default: false },
    existingCount: { type: Number, default: 0 },
    translations: Object,
    labels: Object
  }

  // { file, name, previewUrl, signedId, uploadProgress, status: uploading|complete|error }
  uploadedFiles = []
  selectedImages = new Set()

  connect() {
    this.form = this.element.closest("form")
    if (this.form) {
      this.boundValidateSubmit = this.validateBeforeSubmit.bind(this)
      this.form.addEventListener("submit", this.boundValidateSubmit)
    }
    this.initSortables()
  }

  disconnect() {
    this.uploadedFiles.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl))
    if (this.form && this.boundValidateSubmit) this.form.removeEventListener("submit", this.boundValidateSubmit)
    this.newSortable?.destroy()
    this.existingSortable?.destroy()
  }

  // === Pickers ===

  openCamera() { this.cameraInputTarget.click() }
  openGallery() { this.galleryInputTarget.click() }

  // === Drag to reorder ===

  initSortables() {
    const common = {
      animation: 150,
      delay: 150,               // long-press on touch so the strip still scrolls
      delayOnTouchOnly: true,
      touchStartThreshold: 4,
      ghostClass: "opacity-40",
      filter: "button, input, label",
      preventOnFilter: false
    }
    if (this.hasNewListTarget) {
      this.newSortable = Sortable.create(this.newListTarget, { ...common, onEnd: () => this.syncNewOrderFromDom() })
    }
    if (this.hasExistingListTarget) {
      this.existingSortable = Sortable.create(this.existingListTarget, { ...common, onEnd: () => this.saveExistingOrder() })
    }
  }

  syncNewOrderFromDom() {
    const ordered = Array.from(this.newListTarget.children)
      .map(el => this.uploadedFiles[Number(el.dataset.uploadIndex)])
      .filter(Boolean)
    if (ordered.length === this.uploadedFiles.length) this.uploadedFiles = ordered
    this.updateFilePreview()
  }

  async saveExistingOrder() {
    if (!this.reorderUrlValue) return
    const ids = this.imageCardTargets.map(card => card.dataset.imageId)
    const ok = await this.patchJson(this.reorderUrlValue, { image_ids: ids })
    if (ok) this.refreshBadges()
    else this.showError([this.labelsValue?.save_failed || "Could not save the photo order."])
  }

  async setMainImage(event) {
    event.preventDefault()
    const button = event.currentTarget
    const card = button.closest("[data-image-selector-target~='imageCard']")
    const url = card?.dataset.setMainUrl || button.dataset.url
    if (!url) return

    button.disabled = true
    const ok = await this.patchJson(url, {})
    button.disabled = false
    if (ok) {
      this.existingListTarget.prepend(card)
      this.refreshBadges()
    } else {
      this.showError([this.labelsValue?.save_failed || "Could not save the photo order."])
    }
  }

  // First card wears the MAIN badge, the others a "Make main" button.
  refreshBadges() {
    const labels = this.labelsValue || {}
    this.imageCardTargets.forEach((card, index) => {
      const slot = card.querySelector("[data-image-selector-target~='badgeSlot']")
      if (!slot) return
      if (index === 0) {
        slot.innerHTML = `<span class="px-3 py-1 bg-accent-green text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1 whitespace-nowrap">${labels.main || "MAIN"} <i class="fas fa-check" style="font-size: 12px;"></i></span>`
      } else {
        slot.innerHTML = `<button type="button" class="px-3 py-1 bg-bg-light text-text-primary text-xs font-medium rounded-full shadow-lg flex items-center gap-1 hover:bg-accent-yellow active:scale-95 transition-all border border-gray-200 whitespace-nowrap" data-action="click->image-selector#setMainImage" data-url="${card.dataset.setMainUrl}"><i class="fas fa-star" style="font-size: 9px;"></i> ${labels.set_main || "Set Main"}</button>`
      }
    })
  }

  async patchJson(url, body) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
        body: JSON.stringify(body),
        credentials: "same-origin"
      })
      return response.ok
    } catch (_) {
      return false
    }
  }

  // === Form submission ===

  validateBeforeSubmit(event) {
    const existing = this.imageCardTargets.length || this.existingCountValue
    const completed = this.uploadedFiles.filter(f => f.status === "complete").length

    if (this.requiredValue && existing + completed === 0) {
      event.preventDefault()
      this.showError([this.translationsValue?.product?.images?.required || "At least one photo is required"])
      return false
    }
    if (this.uploadedFiles.some(f => f.status === "uploading")) {
      event.preventDefault()
      this.showError([this.translationsValue?.product?.images?.uploading || "Please wait for all images to finish uploading."])
      return false
    }
    return true
  }

  // === File selection & Direct Upload ===

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    const errors = []
    this.hideError()

    for (const file of files) {
      const isImage = file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name)
      if (!isImage) {
        errors.push(`"${file.name}": ${this.translationsValue?.product?.images?.invalid_type || "Only image files are allowed"}`)
        continue
      }
      if (file.size > this.maxSizeValue) {
        errors.push(`"${file.name}" (${this.formatFileSize(file.size)}): ${this.translationsValue?.product?.images?.too_large || "Image must be less than 10MB"}`)
        continue
      }
      if (this.uploadedFiles.some(f => f.name === file.name && f.file?.size === file.size)) continue

      const entry = { file, name: file.name, previewUrl: URL.createObjectURL(file), signedId: null, uploadProgress: 0, status: "uploading" }
      this.uploadedFiles.push(entry)
      this.startDirectUpload(entry)
    }

    if (errors.length) this.showError(errors)
    if (this.uploadedFiles.length && this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this.updateFilePreview()
    event.target.value = "" // allow picking the same file again
  }

  startDirectUpload(entry) {
    const delegate = {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (e) => {
          if (!e.lengthComputable) return
          entry.uploadProgress = Math.round((e.loaded / e.total) * 100)
          this.updateProgressForFile(entry)
        })
      }
    }
    new DirectUpload(entry.file, this.directUploadUrlValue, delegate).create((error, blob) => {
      if (error) {
        entry.status = "error"
        console.error("Direct upload error:", entry.name, error)
      } else {
        entry.signedId = blob.signed_id
        entry.status = "complete"
        entry.uploadProgress = 100
      }
      this.updateFilePreview()
    })
  }

  removeFile(index) {
    const removed = this.uploadedFiles.splice(index, 1)[0]
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
    this.updateFilePreview()
  }

  // === New-upload thumbnails (hidden signed-id input inside each) ===

  updateFilePreview() {
    if (!this.hasNewPreviewTarget) return
    const count = this.uploadedFiles.length
    this.newPreviewTarget.classList.toggle("hidden", count === 0)
    if (this.hasNewCountTarget) this.newCountTarget.textContent = this.uploadedFiles.filter(f => f.status === "complete").length
    if (!this.hasNewListTarget) return

    this.newListTarget.innerHTML = ""
    this.uploadedFiles.forEach((f, index) => {
      const item = document.createElement("div")
      item.className = "relative inline-block touch-manipulation"
      item.dataset.uploadIndex = index

      const thumb = document.createElement("div")
      thumb.className = "w-24 h-24 rounded-lg overflow-hidden bg-bg-light relative cursor-grab active:cursor-grabbing"
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover pointer-events-none select-none"
      img.alt = f.name
      img.src = f.previewUrl
      img.draggable = false
      thumb.appendChild(img)

      if (f.status === "uploading") {
        const overlay = document.createElement("div")
        overlay.className = "absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center"
        overlay.innerHTML = `<div class="text-white text-xs font-bold" data-role="progress-text">${f.uploadProgress}%</div>`
        const bar = document.createElement("div")
        bar.className = "absolute bottom-0 left-0 h-1 bg-accent-green transition-all duration-300"
        bar.dataset.role = "progress-bar"
        bar.style.width = `${f.uploadProgress}%`
        thumb.appendChild(overlay)
        thumb.appendChild(bar)
      } else if (f.status === "error") {
        const overlay = document.createElement("div")
        overlay.className = "absolute inset-0 bg-red-500 bg-opacity-40 flex items-center justify-center"
        overlay.innerHTML = '<i class="fas fa-exclamation-triangle text-white text-lg"></i>'
        thumb.appendChild(overlay)
      } else {
        const badge = document.createElement("div")
        badge.className = "absolute top-1 left-1 w-5 h-5 bg-accent-green rounded-full flex items-center justify-center"
        badge.innerHTML = '<i class="fas fa-check text-white" style="font-size: 10px;"></i>'
        thumb.appendChild(badge)
        if (index === 0 && !this.hasImageCardTarget) {
          const main = document.createElement("div")
          main.className = "absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-accent-green text-white rounded-full whitespace-nowrap"
          main.style.fontSize = "10px"
          main.textContent = this.labelsValue?.main || "MAIN"
          thumb.appendChild(main)
        }
      }

      if (f.signedId) {
        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = "product[images][]"
        hidden.value = f.signedId
        item.appendChild(hidden)
      }

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 active:scale-95 transition-all shadow-md z-10"
      remove.innerHTML = '<i class="fas fa-times"></i>'
      remove.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); this.removeFile(index) })

      item.appendChild(thumb)
      item.appendChild(remove)
      this.newListTarget.appendChild(item)
    })
  }

  updateProgressForFile(entry) {
    const index = this.uploadedFiles.indexOf(entry)
    if (index === -1 || !this.hasNewListTarget) return
    const item = this.newListTarget.querySelector(`[data-upload-index="${index}"]`)
    if (!item) return
    const text = item.querySelector("[data-role='progress-text']")
    if (text) text.textContent = `${entry.uploadProgress}%`
    const bar = item.querySelector("[data-role='progress-bar']")
    if (bar) bar.style.width = `${entry.uploadProgress}%`
  }

  // === Existing image deletion (confirm modal + Turbo form, unchanged) ===

  toggleSelection(event) {
    const id = event.target.dataset.imageId
    event.target.checked ? this.selectedImages.add(id) : this.selectedImages.delete(id)
    this.updateDeleteButton()
  }

  updateDeleteButton() {
    if (!this.hasDeleteButtonTarget) return
    const count = this.selectedImages.size
    this.deleteCountTarget.textContent = count
    this.deleteButtonTarget.disabled = count === 0
  }

  showSingleDeleteModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = event.currentTarget.dataset.imageId
    if (id) this.openModal(1, [id])
  }

  showDeleteModal(event) {
    event.preventDefault()
    event.stopPropagation()
    this.openModal(this.selectedImages.size, Array.from(this.selectedImages))
  }

  openModal(count, imageIds) {
    const t = this.translationsValue?.product?.images || {}
    const message = count === 1
      ? (t.delete_single || "Are you sure you want to delete this image?")
      : (t.delete_multiple || `Are you sure you want to delete ${count} images?`).replace("%{count}", count)

    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    overlay.id = "delete-confirm-modal"
    overlay.innerHTML = `
      <div class="bg-bg-light rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        <div class="text-center mb-6">
          <div class="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <i class="fas fa-exclamation-triangle text-3xl text-red-600"></i>
          </div>
          <h3 class="text-xl font-bold text-text-primary mb-2">${t.confirm_deletion || "Confirm Deletion"}</h3>
          <p class="text-text-primary opacity-70">${message}</p>
        </div>
        <form method="POST" action="${this.deleteUrlValue}" data-turbo="true" class="flex gap-3">
          <input type="hidden" name="_method" value="delete">
          <input type="hidden" name="authenticity_token" value="${this.getCSRFToken()}">
          ${imageIds.map(id => `<input type="hidden" name="image_ids[]" value="${id}">`).join("")}
          <button type="button" id="cancel-delete-button" class="btn-outline flex-1">${t.cancel || "Cancel"}</button>
          <button type="submit" class="btn-primary flex-1 bg-red-600 hover:bg-red-700">${t.delete || "Delete"}</button>
        </form>
      </div>`
    document.body.appendChild(overlay)
    document.body.style.overflow = "hidden"
    overlay.querySelector("#cancel-delete-button").addEventListener("click", () => this.closeDeleteModal())
    overlay.onclick = (e) => { if (e.target === overlay) this.closeDeleteModal() }
    document.addEventListener("turbo:submit-end", (e) => {
      if (e.detail.success) { setTimeout(() => this.closeDeleteModal(), 200); this.selectedImages.clear(); this.updateDeleteButton() }
    }, { once: true })
  }

  closeDeleteModal() {
    document.getElementById("delete-confirm-modal")?.remove()
    document.body.style.overflow = ""
  }

  // === Utility ===

  getCSRFToken() {
    return document.querySelector("[name='csrf-token']")?.content || ""
  }

  showError(errors) {
    if (!this.hasErrorBoxTarget) return
    this.errorBoxTarget.innerHTML = `
      <div class="flex items-start gap-2">
        <i class="fas fa-exclamation-circle mt-0.5"></i>
        <ul class="list-disc list-inside">${errors.map(e => `<li>${e}</li>`).join("")}</ul>
      </div>`
    this.errorBoxTarget.classList.remove("hidden")
    this.errorBoxTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  hideError() {
    if (this.hasErrorBoxTarget) this.errorBoxTarget.classList.add("hidden")
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / 1048576).toFixed(1) + " MB"
  }
}
